import { useEffect, type ReactElement } from "react";
import { totalWeight, usedCells, type Inventory } from "@rietz/shared";
import {
  clearDialogs,
  closeDialog,
  raiseDialog,
  setModWeapon,
  useHud,
  type DialogKind,
  type OpenContainer,
} from "../store.js";
import { resolveOpenBox } from "../inventory/boxLogic.js";
import { EquipmentDoll } from "../inventory/equipment.js";
import { GridPanel } from "../inventory/grid.js";
import { findInspectItem, InspectDialog } from "../inventory/inspect.js";
import { ContextMenu } from "../inventory/menu.js";
import { FloatingModDialog } from "../inventory/modDialog.js";
import { findOwnedWeapon } from "../inventory/modLogic.js";
import { ModPane } from "../inventory/modPane.js";
import { Ghost, Panel } from "../inventory/panel.js";
import { formatKg } from "../inventory/quickmove.js";
import { ItemTooltip } from "../inventory/tooltip.js";
import { useInventoryDrag } from "../inventory/useInventoryDrag.js";

/**
 * "Gear": the kit you take in, and the stash you take it out of — LITERALLY the
 * raid's inventory screen with a bigger crate: same components, same hook. Only
 * the store's two slots differ (the KIT in `inv`, the STASH in `container`),
 * and `main.ts` routes the ops to the menu's controller. A drag between stash
 * and backpack is an ordinary `applyInventoryOp`.
 */

export interface GearTabProps {
  /** The kit, as displayed (server profile + optimistic ops). */
  inv: Inventory;
  /** The stash, published through the container slot. */
  stash: OpenContainer | null;
}

export function GearTab(props: GearTabProps): ReactElement {
  const { inv, stash } = props;
  const modWeaponIid = useHud((s) => s.modWeaponIid);
  const dialogs = useHud((s) => s.dialogs);
  const dnd = useInventoryDrag(inv, stash, { containerOwned: true });
  const { clear } = dnd;

  // Leaving the tab must not leave a drag armed; it packs the bench and the
  // dialog stack away too.
  useEffect(() => {
    return () => {
      clear();
      setModWeapon(null);
      clearDialogs();
    };
  }, [clear]);

  // Same contract as the raid screen: the pane's subject is re-resolved every
  // render and the pane simply stops being mounted when it fails to resolve.
  const modWeapon = modWeaponIid === null ? null : findOwnedWeapon(inv, modWeaponIid, stash);
  const modGone = modWeaponIid !== null && modWeapon === null;
  useEffect(() => {
    // A stale cleanup queued for a weapon that moved must not close a newer
    // selection made before this effect runs (most visible for stash guns).
    if (modGone && useHud.getState().modWeaponIid === modWeaponIid) setModWeapon(null);
  }, [modGone, modWeaponIid]);

  // ...and every floating dialog, re-resolved every render — selling a box out
  // from under its own window is the case this catches.
  const resolvedDialogs = dialogs.map((ref) => ({
    ref,
    box: ref.t === "box" ? resolveOpenBox(inv, stash, ref.iid) : null,
    inspected: ref.t === "inspect" ? findInspectItem(inv, ref.iid, stash) : null,
  }));
  // Subjects that stopped resolving close their own dialogs. Keyed by a string
  // so
  // the effect fires only when the set of casualties changes.
  const goneKey = resolvedDialogs
    .filter((d) => d.box === null && d.inspected === null)
    .map((d) => `${d.ref.t}:${d.ref.iid}`)
    .join(",");
  useEffect(() => {
    if (goneKey === "") return;
    for (const key of goneKey.split(",")) {
      // First colon only: the kind is ours, the iid is the server's.
      const idx = key.indexOf(":");
      closeDialog(key.slice(0, idx) as DialogKind, key.slice(idx + 1));
    }
  }, [goneKey]);

  return (
    <div
      {...dnd.root}
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        alignContent: "safe center",
        justifyContent: "safe center",
        flexWrap: "wrap",
        // The panels scroll inside the menu rather than the menu as a whole, so
        // the
        // header and tabs stay put.
        minHeight: 0,
        height: "100%",
        width: "100%",
        position: "relative",
        overflowX: "hidden",
        overflowY: "auto",
        touchAction: "none",
      }}
    >
      <Panel
        title="Gear"
        header={
          <>
            <span className="mm-note" style={{ fontSize: 11 }}>
              Drag · R rotate
            </span>
            <span className="mm-num mm-tone-dim" style={{ fontSize: 11.5 }}>
              {formatKg(totalWeight(inv))}
            </span>
          </>
        }
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <EquipmentDoll inv={inv} drag={dnd.drag} {...dnd.tiles} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <GridPanel
              title="Pockets"
              area="pockets"
              grid={inv.pockets}
              drag={dnd.drag}
              {...dnd.tiles}
            />
            {inv.rig === null ? null : (
              <GridPanel title="Tactical rig" area="rig" grid={inv.rig} drag={dnd.drag} {...dnd.tiles} />
            )}
            {inv.backpack === null ? null : (
              <GridPanel
                title="Backpack"
                area="backpack"
                grid={inv.backpack}
                drag={dnd.drag}
                {...dnd.tiles}
              />
            )}
          </div>
        </div>
      </Panel>

      {stash === null ? null : (
        <Panel
          title={stash.name}
          header={
            <>
              <span className="mm-note" style={{ fontSize: 11 }}>
                Ctrl + click: move
              </span>
              <span className="mm-num mm-tone-dim" style={{ fontSize: 11.5 }}>
                {usedCells(stash.grid)}/{stash.grid.w * stash.grid.h}
              </span>
            </>
          }
        >
          <GridPanel
            title=""
            area="container"
            grid={stash.grid}
            drag={dnd.drag}
            {...dnd.tiles}
          />
        </Panel>
      )}

      {/* Inspection is a floating workbench window. The stash deliberately
          remains mounted and interactive behind it, so a part can be picked
          up from the grid and released on an exact mounting-point tile. */}
      {modWeapon === null ? null : (
        <FloatingModDialog
          title={`WEAPONS › ${modWeapon.def.name.toUpperCase()}`}
          rootRef={dnd.root.ref}
          onClose={() => setModWeapon(null)}
        >
          {(contentScale) => (
            <ModPane
              inv={inv}
              container={stash}
              weapon={modWeapon}
              containerLabel="Stash"
              containerOwned
              drag={dnd.drag}
              previewScale={contentScale}
              onTilePointerDown={dnd.tiles.onTilePointerDown}
              onTileContextMenu={dnd.tiles.onTileContextMenu}
            />
          )}
        </FloatingModDialog>
      )}

      {/* The dialogs — opened bags/boxes and item descriptions — float over the
          gear screen. Only the panels themselves intercept input, leaving the
          stash and kit reachable for transfers, so a description can be left up
          while the shelf is being sorted.

          ONE LIST, the two kinds interleaved, because the ORDER IS THE STACKING
          (see `dialogs`): splitting them into two blocks would pin every
          description above every box forever, and being able to put the window
          you are working in on top is the whole point of letting them co-exist.

          AFTER THE BENCH, and that is the point of the placement: the Werkbank is
          a sibling window at the same z-index, so a dialog opened while the bench
          is up must land ON TOP of it rather than under it — and the bench is
          movable for the times it is the thing in the way. */}
      {resolvedDialogs.map(({ ref, box, inspected }, i) => {
        const key = `${ref.t}:${ref.iid}`;
        const nudge = i * 26;
        const raise = () => raiseDialog(ref.t, ref.iid);
        if (ref.t === "box") {
          return box === null ? null : (
            <Panel
              overlay
              key={key}
              nudge={nudge}
              onRaise={raise}
              title={box.def.name}
              header={
                <span className="mm-num mm-tone-dim" style={{ fontSize: 11.5 }}>
                  {box.fill.used}/{box.fill.total}
                </span>
              }
              onClose={() => {
                closeDialog("box", ref.iid);
              }}
            >
              <GridPanel
                title=""
                {...box.target}
                grid={box.grid}
                drag={dnd.drag}
                {...dnd.tiles}
              />
            </Panel>
          );
        }
        return inspected === null ? null : (
          <InspectDialog
            key={key}
            nudge={nudge}
            onRaise={raise}
            subject={inspected}
            onClose={() => {
              closeDialog("inspect", ref.iid);
            }}
          />
        );
      })}

      <Ghost drag={dnd.drag} inv={inv} container={stash} />

      {dnd.tip === null ? null : <ItemTooltip tip={dnd.tip} inv={inv} container={stash} />}

      {dnd.menu === null ? null : (
        <ContextMenu
          menu={dnd.menu}
          inv={inv}
          container={stash}
          place="menu"
          onClose={dnd.closeMenu}
        />
      )}
    </div>
  );
}
